import {useState} from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  FormLayout,
  IndexTable,
  InlineStack,
  Modal,
  Page,
  Text,
  TextField,
} from '@shopify/polaris';
import type {PagePropsBase} from '@slj/ui/document';
import {AppFrame} from '@slj/ui/app-frame';
import {EmptyState} from '@slj/ui/patterns';

import type {ChromeProps} from './dashboard';
import {identityAccount, identitySections} from './nav';

/** One group row, pre-formatted server-side (dates never formatted in render —
 *  workerd and browser ICU disagree; the hydration contract needs identical markup). */
export interface GroupRow {
  id: string;
  name: string;
  memberCount: number;
  appCount: number;
  created: string;
}

export interface GroupsProps extends PagePropsBase {
  chrome: ChromeProps;
  groups: GroupRow[];
  error?: string;
}

const errorMessage = (code: string) =>
  ({
    'name-taken': 'A group with that name already exists.',
    'name-empty': 'A group needs a name.',
  })[code] ?? 'Something went wrong.';

export function GroupsPage({chrome, groups, error}: GroupsProps) {
  const {user} = chrome;
  const [name, setName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<GroupRow | null>(null);

  return (
    <AppFrame
      logoUrl="/assets/logo.svg"
      user={user}
      sections={identitySections(user.isAdmin)}
      currentPath={chrome.path}
      account={identityAccount}
    >
      <Page title="Groups" fullWidth>
        <BlockStack gap="500">
          {error ? (
            <Banner tone="critical">
              <p>{errorMessage(error)}</p>
            </Banner>
          ) : null}

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                New group
              </Text>
              <Text as="p" tone="subdued">
                A group is a named set of people. Grant a group access to an app and every
                member gets it — the simplest way to give a handful of friends the same apps.
              </Text>
              <form method="post" action="/admin/groups">
                <FormLayout>
                  <InlineStack gap="300" wrap={false} blockAlign="end">
                    <div style={{flexGrow: 1}}>
                      <TextField
                        label="Name"
                        id="name"
                        name="name"
                        autoComplete="off"
                        placeholder="e.g. Household"
                        value={name}
                        onChange={setName}
                      />
                    </div>
                    <Button submit variant="primary">
                      Create group
                    </Button>
                  </InlineStack>
                </FormLayout>
              </form>
            </BlockStack>
          </Card>

          <Card padding="0">
            {groups.length === 0 ? (
              <EmptyState>No groups yet.</EmptyState>
            ) : (
              <IndexTable
                resourceName={{singular: 'group', plural: 'groups'}}
                itemCount={groups.length}
                selectable={false}
                headings={[{title: 'Name'}, {title: 'Members'}, {title: 'Apps'}, {title: 'Created'}, {title: ''}]}
              >
                {groups.map((g, index) => (
                  <IndexTable.Row id={g.id} key={g.id} position={index}>
                    <IndexTable.Cell>
                      <a
                        data-primary-link
                        href={`/admin/groups/${g.id}`}
                        style={{textDecoration: 'none', color: 'inherit'}}
                      >
                        <Text as="span" fontWeight="semibold">
                          {g.name}
                        </Text>
                      </a>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text as="span" tone="subdued">{`${g.memberCount}`}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      {g.appCount ? <Badge>{`${g.appCount}`}</Badge> : <Text as="span" tone="subdued">none</Text>}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text as="span" tone="subdued">{g.created}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <InlineStack gap="200" align="end" wrap={false}>
                        <Button size="slim" url={`/admin/groups/${g.id}`}>
                          Manage
                        </Button>
                        {/* stopPropagation: the row's primary link navigates; delete must not. */}
                        <div onClick={(e) => e.stopPropagation()}>
                          <Button size="slim" tone="critical" onClick={() => setDeleteTarget(g)}>
                            Delete
                          </Button>
                        </div>
                      </InlineStack>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                ))}
              </IndexTable>
            )}
          </Card>
        </BlockStack>
      </Page>

      {deleteTarget ? (
        <Modal open onClose={() => setDeleteTarget(null)} title={`Delete “${deleteTarget.name}”?`}>
          <Modal.Section>
            <form method="post" action="/admin/groups/delete">
              <input type="hidden" name="id" value={deleteTarget.id} />
              <BlockStack gap="400">
                <Text as="p">
                  The group and its app grants are removed. Members keep any access they
                  hold directly or through other groups — only this group&rsquo;s grants go.
                </Text>
                <InlineStack align="end" gap="200">
                  <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
                  <Button submit variant="primary" tone="critical">
                    Delete group
                  </Button>
                </InlineStack>
              </BlockStack>
            </form>
          </Modal.Section>
        </Modal>
      ) : null}
      <Box />
    </AppFrame>
  );
}
